import { GridWrapper } from "./ImageGrid.styled";
import { BREAKPOINTS } from "src/breakpoints";
import classNames from "classnames";
import { useCallback } from "react";
import { ProductLabels } from "../ProductLabels";
import { ProductLabel } from "src/types/graphql.d";
import { useImageCarouselDispatch } from "src/hooks/context/use-image-carousel-context";
import { GridImage } from "./GridImage";

type ImageGridProps = {
  photos: string[];
  labels?: ProductLabel[];
  productName: string;
  setIsZoomOpen: (isZoomOpen: boolean) => void;
};

// See docs/tech/responsive-images.md for how these were derived.
const HALF_SLOT_SIZES = `(max-width: ${BREAKPOINTS.lg}) 50vw, 440px`;
const FULL_SLOT_SIZES = `(max-width: ${BREAKPOINTS.lg}) 100vw, 860px`;

function isFullWidthSlot(photoCount: number, index: number): boolean {
  if (photoCount === 3 || photoCount === 5 || photoCount === 7) {
    return index === 2;
  }

  if (photoCount === 8) {
    return index === 5;
  }

  return false;
}

function ImageGrid({
  photos,
  labels,
  productName,
  setIsZoomOpen,
}: ImageGridProps): JSX.Element {
  const imageCarouselDispatch = useImageCarouselDispatch();
  const handleImageClick = useCallback(
    (index: number) => {
      setIsZoomOpen(true);
      imageCarouselDispatch({
        type: "SET_SELECTED_IMAGE_INDEX",
        payload: {
          selectedImageIndex: index,
          updateIdentifier: "zoom",
        },
      });
    },
    [setIsZoomOpen, imageCarouselDispatch]
  );

  return (
    <GridWrapper
      className={classNames({
        ["two-by-two"]: photos.length % 4 === 0 || photos.length % 2 === 0,
        ["two-by-one"]:
          photos.length === 3 || photos.length === 5 || photos.length === 7,
        ["two-by-three"]: photos.length === 8,
      })}
    >
      {photos.map((photo, index) => (
        <GridImage
          key={`pdp-${productName}-${index}`}
          photo={photo}
          productName={productName}
          index={index}
          sizes={
            isFullWidthSlot(photos.length, index)
              ? FULL_SLOT_SIZES
              : HALF_SLOT_SIZES
          }
          handleImageClick={handleImageClick}
        >
          {labels && index === 0 ? (
            <div className="labels-wrapper">
              <ProductLabels labels={labels} />
            </div>
          ) : null}
        </GridImage>
      ))}
    </GridWrapper>
  );
}

export { ImageGrid };
